import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, Mountain } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useBooking } from "@/context/BookingContext";

const IMG = {
  sunset: "/images/harkidun-sunset.webp",
  homestay: "/images/harkidun-homestay.webp",
  trekkers: "/images/harkidun-trekkers.webp",
  trailRest: "/images/harkidun-trail-rest.webp",
  lunch: "/images/harkidun-lunch.webp",
};

const ITINERARY = [
  {
    day: "Day 01",
    title: "Dehradun → Sankri → Osla",
    body: "Arrive at Osla by evening after a long, scenic drive. A warm welcome awaits with tea and refreshments. Get acquainted at our homestay — a traditional Garhwali house — and wind down with great views and a cool mountain breeze, followed by a local-cuisine dinner. Rest well for the days ahead.",
    image: IMG.homestay,
  },
  {
    day: "Day 02",
    title: "Osla → Boslo Campsite",
    meta: "~3,300 m / 10,800 ft · 10 km trek",
    body: "Trek to Boslo with packed lunches on the go. Arrive at camp to warm tea and coffee waiting for you, followed by a soulful dinner in the mountains and cozy tents to ensure a good night's sleep after a long day.",
    image: IMG.trekkers,
  },
  {
    day: "Day 03",
    title: "Boslo → Har Ki Dun → Ratadoh (via Maninda Taal)",
    body: "Wake to a yoga session in fresh mountain air and stretch for the big day. Trek through boreal forest to Har Ki Dun with mighty views of the Borasu Glacier and surrounding peaks, returning via Maninda Taal. Packed lunches on the go. Back to camp by evening — relax with warm tea, dinner, and rest.",
    image: IMG.trailRest,
  },
  {
    day: "Day 04",
    title: "Boslo → Osla (descent)",
    body: "Morning yoga and a good stretch before the downhill descent to Osla, with packed lunch along the way. Reach Osla by evening to refreshments and warm tea. Sink into the memories of the past days, enjoy local-cuisine dinner once more, and cozy beds in the traditional house at Osla.",
    image: IMG.lunch,
  },
  {
    day: "Day 05",
    title: "Osla → Sankri → Aruha Cafe",
    body: "Morning yoga and breakfast, then drive back to Sankri. Visit us at our cafe & stay — a short 3 km hike from Sankri main market — to share your experience over pizza and coffee (optional), or drive directly back to Dehradun.",
    image: IMG.homestay,
  },
];

export default function HarKiDun() {
  const { openTour } = useBooking();
  const book = () => openTour("Har Ki Dun Trek");

  return (
    <div className="bg-[#f4f1ea] text-[#23281f]">
      <Navbar />

      {/* Hero */}
      <section data-testid="trek-hero" className="relative h-[92vh] w-full overflow-hidden bg-[#23281f]">
        <img src={IMG.sunset} alt="Har Ki Dun sunset" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "center 40%" }} />
        <div className="absolute inset-0 bg-[#12160f]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d110d] via-[#0d110d]/25 to-[#0d110d]/55" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-20 lg:px-10">
          <a href="/#adventures" className="mb-6 inline-flex w-fit items-center gap-2 text-sm text-[#e4e2da] hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All experiences
          </a>
          <div className="mb-4 inline-block w-fit rounded-none bg-black/45 px-3 py-1.5 backdrop-blur-sm">
            <span className="overline !text-[#f2e4c9]">Trekking &amp; Tours</span>
          </div>
          <h1 className="max-w-3xl font-serif text-5xl font-medium leading-[0.95] tracking-tight text-[#f6f3ec] text-shadow-soft lg:text-8xl">
            Har Ki Dun <span className="italic text-[#e7d2ad]">Trek</span>
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-[#e4e2da]">
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-[#e7d2ad]" /> 5 Days / 4 Nights</span>
            <span className="inline-flex items-center gap-2"><Mountain className="h-4 w-4 text-[#e7d2ad]" /> Max ~3,566 m</span>
            <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[#e7d2ad]" /> Sankri · Osla · Har Ki Dun Valley</span>
          </div>
          <button
            data-testid="trek-book-btn"
            onClick={book}
            className="mt-8 inline-flex w-fit rounded-none bg-[#ba5a45] px-9 py-4 text-xs font-semibold uppercase tracking-widest text-[#f6f3ec] transition-colors hover:bg-[#9c4633]"
          >
            Enquire / Book this trek
          </button>
        </div>
      </section>

      {/* Overview */}
      <section className="px-6 py-24 lg:px-10">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="overline mb-6">The Journey</div>
            <p className="font-serif text-2xl leading-relaxed text-[#3a4038] lg:text-3xl">
              A gentle yet soul-stirring walk into the Har Ki Dun valley — traditional Garhwali homestays,
              boreal forests, glacier views, and mornings that begin with yoga in the thin mountain air.
              Warm meals, packed lunches on the trail, and true mountain hospitality throughout.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Itinerary */}
      <section className="bg-[#eae4d8] px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-[1200px]">
          <Reveal>
            <div className="overline mb-6">Day-by-day</div>
            <h2 className="mb-16 font-serif text-4xl font-medium tracking-tight text-[#23281f] lg:text-6xl">
              The <span className="italic text-[#ba5a45]">itinerary.</span>
            </h2>
          </Reveal>

          <div className="space-y-16 lg:space-y-24">
            {ITINERARY.map((d, i) => (
              <motion.div
                key={d.day}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-14 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={d.image} alt={d.title} className="h-full w-full object-cover" style={{ objectPosition: "center 45%" }} />
                </div>
                <div>
                  <div className="font-serif text-6xl leading-none text-[#c9bfa9] lg:text-7xl">{d.day.split(" ")[1]}</div>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#ba5a45]">{d.day}</div>
                  <h3 className="mt-3 font-serif text-3xl text-[#23281f] lg:text-4xl">{d.title}</h3>
                  {d.meta && <div className="mt-2 text-sm font-medium text-[#8a7a62]">{d.meta}</div>}
                  <p className="mt-4 max-w-xl leading-relaxed text-[#5c6157]">{d.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 py-24 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="overline mb-6">From the trail</div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
            {[IMG.homestay, IMG.trekkers, IMG.trailRest, IMG.lunch].map((src, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06} className="group aspect-[3/4] overflow-hidden">
                <img src={src} alt="Har Ki Dun trek" className="img-zoom h-full w-full object-cover" />
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-col items-center gap-6 border-t border-[#ddd6c8] pt-14 text-center">
              <h3 className="font-serif text-3xl text-[#23281f] lg:text-5xl">Ready for the valley of gods?</h3>
              <button
                data-testid="trek-book-btn-bottom"
                onClick={book}
                className="rounded-none bg-[#ba5a45] px-10 py-4 text-xs font-semibold uppercase tracking-widest text-[#f6f3ec] transition-colors hover:bg-[#9c4633]"
              >
                Enquire / Book this trek
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
